import { Row, Col, Card, Typography, Spin, Alert, Divider, theme } from 'antd';
import {
  UserOutlined,
  SwapOutlined,
  TrophyOutlined,
  FileSearchOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  SafetyCertificateOutlined,
  CheckCircleOutlined,
  CalendarOutlined,
  RiseOutlined,
  PercentageOutlined,
} from '@ant-design/icons';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  PieChart,
  Pie,
  Legend,
} from 'recharts';
import dayjs from 'dayjs';
import { useDashboardMetrics } from '@/hooks/useDashboard';
import { StatCard } from '@/components/StatCard';
import { SPORT_TYPE_CONFIG } from '@/config/constants';

const SPORT_PIE_COLORS = [
  '#1890ff', '#52c41a', '#fa8c16', '#722ed1', '#13c2c2',
  '#eb2f96', '#faad14', '#f5222d', '#2f54eb', '#a0d911',
];

const { Title, Text } = Typography;

const SCORE_HISTOGRAM_COLORS = ['#CD7F32', '#CD7F32', '#C0C0C0', '#C0C0C0', '#FFD700', '#FFD700', '#E5E4E2'];

export function DashboardPage() {
  const { token } = theme.useToken();
  const { data: metrics, isLoading, error } = useDashboardMetrics();

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" tip="대시보드 로딩 중..." />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="데이터 로드 실패"
        description="대시보드 데이터를 불러오지 못했습니다. 잠시 후 다시 시도해주세요."
        type="error"
        showIcon
      />
    );
  }

  // 실제 API 데이터 사용, 데이터 없을 시 0 또는 빈 배열로 초기화 (가짜 수치 없음)
  const cumulativeData = metrics?.cumulative ?? {
    totalUsers: 0,
    autoMatchUsers: 0,
    verifiedUsers: 0,
    completedGames: 0,
  };

  const realtimeData = metrics?.realtime ?? {
    activeUsers: 0,
    activeMatchRequests: 0,
    ongoingMatches: 0,
    pendingResultVerifications: 0,
  };

  const todayData = metrics?.today ?? {
    newSignups: 0,
    matchesCreated: 0,
    matchesCompleted: 0,
    reportsReceived: 0,
  };

  const activityData = metrics?.activity ?? { wau: 0, mau: 0 };

  const dauTrend = metrics?.charts?.dauTrend ?? [];

  const signupTrend = metrics?.charts?.signupTrend ?? [];

  const scoreDistribution = metrics?.charts?.scoreDistribution?.buckets ?? [];

  const sportDistribution = metrics?.charts?.sportDistribution ?? [];

  // 종목 코드 → 한글 라벨 매핑 (파이 차트용)
  const sportPieData = sportDistribution.map((s) => ({
    label: SPORT_TYPE_CONFIG[s.sportType as keyof typeof SPORT_TYPE_CONFIG]?.label ?? s.sportType,
    count: s.count,
  }));

  const matchSuccessRate = metrics?.charts?.matchSuccessRate ?? 0;

  // 자동매칭 전환율 = ON 사용자 / 전체 사용자
  const autoMatchRate =
    cumulativeData.totalUsers > 0
      ? (cumulativeData.autoMatchUsers / cumulativeData.totalUsers) * 100
      : 0;

  return (
    <div>
      <Title level={4} style={{ marginBottom: 8 }}>
        대시보드
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        마지막 갱신: {dayjs().format('YYYY-MM-DD HH:mm:ss')} (30초마다 자동 갱신)
      </Text>

      {/* 누적 현황 카드 */}
      <Title level={5} style={{ marginBottom: 12 }}>
        누적 현황
      </Title>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="총 사용자"
            value={cumulativeData.totalUsers}
            prefix={<TeamOutlined />}
            color="#1890ff"
            suffix="명"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="자동매칭 ON 사용자"
            value={cumulativeData.autoMatchUsers}
            prefix={<ThunderboltOutlined />}
            color="#722ed1"
            suffix="명"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="본인인증 완료"
            value={cumulativeData.verifiedUsers}
            prefix={<SafetyCertificateOutlined />}
            color="#13c2c2"
            suffix="명"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="누적 경기 완료"
            value={cumulativeData.completedGames}
            prefix={<CheckCircleOutlined />}
            color="#52c41a"
            suffix="경기"
          />
        </Col>
      </Row>

      {/* 활성 사용자 · 전환 */}
      <Title level={5} style={{ marginBottom: 12 }}>
        활성 사용자 · 전환
      </Title>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={8}>
          <StatCard
            title="WAU (최근 7일 활성)"
            value={activityData.wau}
            prefix={<CalendarOutlined />}
            color="#1890ff"
            suffix="명"
          />
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <StatCard
            title="MAU (최근 30일 활성)"
            value={activityData.mau}
            prefix={<RiseOutlined />}
            color="#2f54eb"
            suffix="명"
          />
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <StatCard
            title="자동매칭 전환율"
            value={autoMatchRate.toFixed(1)}
            prefix={<PercentageOutlined />}
            color="#722ed1"
            suffix="%"
          />
        </Col>
      </Row>

      {/* 실시간 현황 카드 */}
      <Title level={5} style={{ marginBottom: 12 }}>
        실시간 현황
      </Title>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="현재 접속자"
            value={realtimeData.activeUsers}
            prefix={<UserOutlined />}
            color="#1890ff"
            suffix="명"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="활성 매칭 요청"
            value={realtimeData.activeMatchRequests}
            prefix={<SwapOutlined />}
            color="#52c41a"
            suffix="건"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="진행중 매칭"
            value={realtimeData.ongoingMatches}
            prefix={<TrophyOutlined />}
            color="#fa8c16"
            suffix="건"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="결과 인증 대기"
            value={realtimeData.pendingResultVerifications}
            prefix={<FileSearchOutlined />}
            color="#f5222d"
            suffix="건"
          />
        </Col>
      </Row>

      {/* 오늘의 지표 */}
      <Title level={5} style={{ marginBottom: 12 }}>
        오늘의 지표
      </Title>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small" style={{ borderRadius: 8, textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#1890ff' }}>
              +{todayData.newSignups}
            </div>
            <div style={{ color: token.colorTextSecondary, fontSize: 13 }}>신규 가입</div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small" style={{ borderRadius: 8, textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#52c41a' }}>
              {todayData.matchesCreated}
            </div>
            <div style={{ color: token.colorTextSecondary, fontSize: 13 }}>매칭 생성</div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small" style={{ borderRadius: 8, textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#fa8c16' }}>
              {todayData.matchesCompleted}
            </div>
            <div style={{ color: token.colorTextSecondary, fontSize: 13 }}>경기 완료</div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small" style={{ borderRadius: 8, textAlign: 'center' }}>
            <div
              style={{
                fontSize: 28,
                fontWeight: 700,
                color: todayData.reportsReceived > 5 ? '#f5222d' : token.colorText,
              }}
            >
              {todayData.reportsReceived}
            </div>
            <div style={{ color: token.colorTextSecondary, fontSize: 13 }}>신고 접수</div>
          </Card>
        </Col>
      </Row>

      <Divider />

      {/* 차트 영역 */}
      <Row gutter={[16, 16]}>
        {/* DAU 추이 차트 */}
        <Col xs={24} lg={16}>
          <Card
            title="DAU 추이 (최근 30일)"
            style={{ borderRadius: 8 }}
            extra={
              <Text type="secondary" style={{ fontSize: 12 }}>
                일별 활성 사용자
              </Text>
            }
          >
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={dauTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  interval={6}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => v.toLocaleString()}
                />
                <Tooltip
                  formatter={(val: number) => [val.toLocaleString() + '명', 'DAU']}
                  labelFormatter={(label) => `${label}`}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#1890ff"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        {/* 매칭 성사율 */}
        <Col xs={24} lg={8}>
          <Card title="매칭 성사율" style={{ borderRadius: 8, height: '100%' }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '40px 0',
              }}
            >
              <div
                style={{
                  width: 140,
                  height: 140,
                  borderRadius: '50%',
                  border: `12px solid ${matchSuccessRate >= 0.6 ? '#52c41a' : '#fa8c16'}`,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: token.colorFillAlter,
                }}
              >
                <span
                  style={{
                    fontSize: 32,
                    fontWeight: 700,
                    color: matchSuccessRate >= 0.6 ? '#52c41a' : '#fa8c16',
                  }}
                >
                  {(matchSuccessRate * 100).toFixed(1)}%
                </span>
              </div>
              <Text
                type="secondary"
                style={{ marginTop: 16, textAlign: 'center', fontSize: 13 }}
              >
                목표: 60% 이상
                <br />
                {matchSuccessRate >= 0.6 ? '목표 달성' : '목표 미달성'}
              </Text>
            </div>
          </Card>
        </Col>

        {/* 신규 가입 추이 막대 차트 */}
        <Col xs={24} lg={16}>
          <Card
            title="신규 가입 추이 (최근 30일)"
            style={{ borderRadius: 8 }}
            extra={
              <Text type="secondary" style={{ fontSize: 12 }}>
                일별 가입자
              </Text>
            }
          >
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={signupTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  interval={6}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />
                <Tooltip formatter={(val: number) => [val.toLocaleString() + '명', '신규 가입']} />
                <Bar dataKey="value" fill="#52c41a" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        {/* 종목별 사용자 분포 파이 차트 */}
        <Col xs={24} lg={8}>
          <Card title="종목별 사용자 분포" style={{ borderRadius: 8, height: '100%' }}>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={sportPieData}
                  dataKey="count"
                  nameKey="label"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  label={(entry: { label: string; count: number }) => `${entry.label} ${entry.count}`}
                  labelLine={false}
                >
                  {sportPieData.map((_, index) => (
                    <Cell key={index} fill={SPORT_PIE_COLORS[index % SPORT_PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(val: number) => [val.toLocaleString() + '명']} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        {/* 점수 분포 히스토그램 */}
        <Col xs={24}>
          <Card
            title="점수 분포 히스토그램"
            style={{ borderRadius: 8 }}
            extra={
              <div style={{ display: 'flex', gap: 12, fontSize: 12 }}>
                <span style={{ color: '#CD7F32' }}>■ 브론즈</span>
                <span style={{ color: '#C0C0C0' }}>■ 실버</span>
                <span style={{ color: '#FFD700' }}>■ 골드</span>
                <span style={{ color: '#888' }}>■ 플래티넘</span>
              </div>
            }
          >
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={scoreDistribution} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="rangeStart"
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => `${v}`}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => v.toLocaleString()}
                />
                <Tooltip
                  formatter={(val: number, _: string, props: { payload?: { rangeStart: number; rangeEnd: number } }) => [
                    val.toLocaleString() + '명',
                    `${props.payload?.rangeStart}~${props.payload?.rangeEnd}점`,
                  ]}
                />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {scoreDistribution.map((_, index) => (
                    <Cell
                      key={index}
                      fill={SCORE_HISTOGRAM_COLORS[Math.min(index, SCORE_HISTOGRAM_COLORS.length - 1)]}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
